import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Animated month accordions for the PDP expectations section.
 *
 * Native <details> drops its content the instant `open` is removed, so on close the `is-open`
 * class goes first (collapsing the grid row) and `open` only follows once the transition has
 * finished. The first item is server-rendered with both `open` and `is-open`, so it is visible at
 * first paint; `connectedCallback` only catches up items the native toggle opened before this
 * module ran.
 *
 * @extends {Component}
 */
class PdpExpections extends Component {
  connectedCallback() {
    super.connectedCallback();

    for (const details of this.querySelectorAll('details[open]:not(.is-open)')) {
      details.classList.add('is-open');
    }
  }

  /**
   * @param {MouseEvent} event
   */
  toggle(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const details = event.target.closest('details');
    if (!details) return;

    event.preventDefault();

    // Neither `is-open` (added two frames after an open starts, removed before a close ends) nor
    // `details.open` (kept until the close transition ends) says where the item is heading, so the
    // target state is tracked synchronously in `data-expanded`.
    const expanded = details.dataset.expanded ? details.dataset.expanded === 'true' : details.open;

    if (expanded) {
      this.#close(details);
    } else {
      this.#open(details);
    }
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #open(details) {
    details.dataset.expanded = 'true';
    details.open = true;

    // Two frames, so the collapsed 0fr row is painted before transitioning to 1fr.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (details.dataset.expanded === 'true') details.classList.add('is-open');
      })
    );
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    details.dataset.expanded = 'false';
    details.classList.remove('is-open');

    const text = details.querySelector(':scope > .text');

    if (!text || prefersReducedMotion()) {
      details.open = false;
      return;
    }

    /** @param {TransitionEvent} event */
    const settle = (event) => {
      // Transitions inside the content (a link hover, say) bubble up here too; only ours counts.
      if (event.target !== text) return;

      text.removeEventListener('transitionend', settle);
      text.removeEventListener('transitioncancel', settle);

      // Reopened while the close transition was still running → leave `open` alone.
      if (details.dataset.expanded !== 'true') details.open = false;
    };

    text.addEventListener('transitionend', settle);
    text.addEventListener('transitioncancel', settle);
  }
}

if (!customElements.get('pdp-expections')) {
  customElements.define('pdp-expections', PdpExpections);
}
